import type { MessageDeliveryStatus } from '../hooks/use-chat';

/**
 * WhatsApp-style delivery indicator rendered after the timestamp on own
 * message bubbles. Three states, driven by `deriveMessageStatus`:
 *   SENT      → single gray tick (server hasn't confirmed yet)
 *   DELIVERED → double gray tick (server accepted + broadcast)
 *   READ      → double blue tick (counter-party has read)
 *
 * Inline SVGs keep this dependency-free and match the icon style used
 * elsewhere in the chat module.
 */
export function MessageTicks({ status }: { status: MessageDeliveryStatus }) {
  const isRead = status === 'READ';
  const isDouble = status !== 'SENT';
  // Blue when read, neutral gray-500 otherwise. The colours match the
  // surrounding meta-row text so the ticks read as part of the timestamp.
  const color = isRead ? '#3b82f6' : 'currentColor';
  const label = status === 'READ' ? 'Read' : status === 'DELIVERED' ? 'Delivered' : 'Sent';

  return (
    <span className="ml-0.5 inline-flex items-center" aria-label={label} title={label}>
      <svg
        width={isDouble ? 14 : 10}
        height={10}
        viewBox={isDouble ? '0 0 16 10' : '0 0 10 10'}
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {isDouble ? (
          <>
            {/* Back tick (rear check) */}
            <polyline points="1 5.5 4 8.5 9 2.5" />
            {/* Front tick — slightly to the right for the double effect */}
            <polyline points="6 5.5 9 8.5 14.5 2.5" />
          </>
        ) : (
          <polyline points="1 5.5 4 8.5 9 2.5" />
        )}
      </svg>
    </span>
  );
}
